<template>
  <MenuList>
    <MenuListItem icon="Move" @click="handleMoveClick"> Move </MenuListItem>
    <MenuListItem
      icon="Lock"
      :disabled="!canChangePrivacy"
      @click="handlePrivacyControlsClick"
    >
      {{ privacyControlsCopy }}
    </MenuListItem>
    <MenuListItem
      class="text-red-600"
      :disabled="!canArchive"
      @click="handleArchiveClick"
    >
      <Icon name="Archive" :size="18" class="mr-3 stroke-red-600" />
      Archive
    </MenuListItem>
  </MenuList>
</template>

<script setup lang="ts">
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { FilePermission, type File } from '@/shared/graphql/router/graphql'
import { MenuList, MenuListItem } from '@/shared/components/Menu'
import { computed } from 'vue'
import { Icon } from '@dacr/ui'

const props = defineProps<{
  files: File[]
}>()
const emit = defineEmits<{
  clicked: []
}>()

const { open } = useDialog()

const canChangePrivacy = computed(() =>
  props.files.every((file) => file.permissions.includes(FilePermission.Privacy))
)

const canArchive = computed(() =>
  props.files.every((file) => file.permissions.includes(FilePermission.Archive))
)

const privacyControlsCopy = computed(() => {
  const allFilesPublic = props.files.every((file) => !file.settings?.isPrivate)
  const allFilesPrivate = props.files.every((file) => file.settings?.isPrivate)

  if (allFilesPublic) return 'Make files private'
  if (allFilesPrivate) return 'Make files public'
  return 'Privacy controls'
})

const handleMoveClick = () => {
  document.body.click()

  open(Dialogs.MoveItems, {
    props: { ids: props.files.map((file) => file.id) },
  })
  emit('clicked')
}

const handlePrivacyControlsClick = () => {
  const allFilesPublic = props.files.every((file) => !file.settings?.isPrivate)
  const allFilesPrivate = props.files.every((file) => file.settings?.isPrivate)
  let dialog = Dialogs.UpdateFilePrivacy

  document.body.click()

  if (allFilesPublic) dialog = Dialogs.MakeFilesPrivate
  if (allFilesPrivate) dialog = Dialogs.MakeFilesPublic

  open(dialog, {
    props: { ids: props.files.map((file) => file.id) },
  })
  emit('clicked')
}

const handleArchiveClick = () => {
  document.body.click()

  open(Dialogs.ArchiveFiles, { props: { files: props.files } })
  emit('clicked')
}
</script>
